import React, { useEffect } from 'react';

/**
 * MobileModal — viewport-safe modal shell that works correctly on small
 * screens (especially when the keyboard pops up).
 *
 * Why it exists:
 *  - The previous pattern `fixed inset-0 flex items-center justify-center`
 *    centers the modal vertically. On mobile, when the soft keyboard opens
 *    the viewport shrinks, and the centred modal slides ABOVE the visible
 *    area with no way to scroll back to it.
 *  - This shell pins the modal to the top on small screens and only
 *    centers vertically on `sm+` (>=640px). The OUTER element is the
 *    scroll container so the entire modal stays reachable regardless of
 *    keyboard, content height, or viewport size.
 *
 * Usage:
 *   <MobileModal onClose={close} maxWidth="md" testId="my-modal">
 *     <header className="px-5 py-4 ...">Title</header>
 *     <div className="p-5 ...">Body</div>
 *   </MobileModal>
 */
const WIDTH = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
};

export default function MobileModal({
  onClose,
  maxWidth = 'md',
  testId,
  closeOnBackdrop = true,
  className = '',
  children,
}) {
  // Lock body scroll while open so background page doesn't double-scroll.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Esc key closes.
  useEffect(() => {
    if (!onClose) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const widthCls = WIDTH[maxWidth] || WIDTH.md;

  return (
    <div
      className="fixed inset-0 bg-black/75 z-50 overflow-y-auto"
      data-testid={testId}
      onClick={(e) => {
        if (closeOnBackdrop && onClose && e.target === e.currentTarget) onClose();
      }}
    >
      {/* Inner wrapper centers on sm+, pins to top on mobile so keyboard never
          hides the modal. py-4/sm:py-8 leaves breathing room from edges. */}
      <div
        className="min-h-full flex items-start sm:items-center justify-center p-2 sm:p-4 py-4 sm:py-8"
        onClick={(e) => {
          if (closeOnBackdrop && onClose && e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className={`bg-zinc-900 border border-zinc-700 rounded-2xl w-full ${widthCls} my-2 sm:my-0 ${className}`}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
